/**
 * FRIDGE-874 — one builder for the row shape the Saved + Compare store stores.
 *
 * The homepage rails hand the store a `HomeProduct` and already have every field.
 * Every other product surface — collection and brand grids, filter and article
 * rails, the deals grid, the product page buy box — carries a DIFFERENT shape:
 * some have the row id, some only the canonical path; some have cents, most have
 * an already-formatted price string like "$1,299". This module turns any of them
 * into the same `SavedItem`, so a refrigerator saved from a collection card and
 * the same refrigerator ticked on its own page are one entry, not two.
 *
 * Identity: `id` when the caller has the row id, otherwise the canonical path.
 * The store matches on EITHER (see saved-compare-store.ts `matches`), so the two
 * never split.
 *
 * Pure and dependency-light on purpose: card components call it during render,
 * and on the server-rendered surfaces (the deals grid, the carousel card) it runs
 * on the server and never ships at all.
 */
import { finFromProductHref } from '@/lib/card-link-names'
import type { SavedItem } from '@/components/homepage-2026/saved-compare-store'

export type { SavedItem }

/**
 * Cents from a rendered price string — "$1,299" → 129900, "$1,299.50" → 129950.
 * Anything unparseable or non-positive is 0, which is exactly what the panels and
 * the saved page already treat as "no price to show".
 */
export function priceTextToCents(text: string | null | undefined): number {
  if (typeof text !== 'string') return 0
  const m = text.replace(/,/g, '').match(/\d+(?:\.\d+)?/)
  if (!m) return 0
  const n = Number(m[0])
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0
}

export interface SavedItemInput {
  /** The products row id when the surface has it; the canonical path is used when it does not. */
  id?: string | null
  /** (FIN) Fridge.com Identification Number, when the surface carries it directly. */
  fin?: string | null
  brand?: string | null
  title: string
  image?: string | null
  /** The canonical on-site path — `/products/<slug>`, `/parts/<slug>`, `/filters/<slug>`. */
  href: string
  /** Already-formatted price ("$1,299"); ignored when `priceCents` is given. */
  price?: string | null
  priceCents?: number | null
}

export function buildSavedItem(input: SavedItemInput): SavedItem {
  const href = input.href || ''
  return {
    id: (input.id ?? '').trim() || href,
    // An appliance URL already encodes its FIN, so most surfaces need pass nothing.
    fin: (input.fin ?? '').trim() || finFromProductHref(href) || '',
    brand: (input.brand ?? '').trim(),
    title: input.title,
    image: input.image || '',
    href,
    priceCents:
      typeof input.priceCents === 'number' && input.priceCents > 0
        ? Math.round(input.priceCents)
        : priceTextToCents(input.price),
  }
}
